/**
 * Phase 1.7 — low-stock warning widget.
 *
 * Surfaces inventory items whose current_quantity has dropped below the
 * configured low_stock_threshold. Empty state is "All stocked" with a
 * positive tone — visual reassurance, not a blank box.
 */

import { Package, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, EmptyState } from '@/components/v2';
import type { DashboardSummary } from '@shared/types';

export interface LowStockCardProps {
  items: DashboardSummary['lowStockItems'];
}

export function LowStockCard({ items }: LowStockCardProps): JSX.Element {
  if (items.length === 0) {
    return (
      <Card title="Low stock" subtitle="Below threshold">
        <EmptyState
          variant="card"
          icon={Package}
          title="All stocked"
          description="No inventory items below their low-stock threshold."
        />
      </Card>
    );
  }
  return (
    <Card
      title="Low stock"
      subtitle={`${items.length} item${items.length === 1 ? '' : 's'} below threshold`}
      badge={
        <span className="rounded-full bg-clay/10 px-2 py-0.5 font-mono text-2xs uppercase tracking-wider text-clay dark:bg-clay-dark/20 dark:text-clay-dark">
          <AlertTriangle className="-mt-0.5 mr-1 inline h-3 w-3" />
          Action
        </span>
      }
    >
      <ul className="divide-y divide-border">
        {items.slice(0, 6).map((i) => {
          const pct =
            i.low_stock_threshold && i.low_stock_threshold > 0
              ? Math.min(100, Math.round((i.current_quantity / i.low_stock_threshold) * 100))
              : null;
          return (
            <li key={i.id} className="py-2.5 first:pt-0 last:pb-0">
              <div className="flex items-center justify-between gap-3">
                <Link
                  to="/inventory"
                  className="min-w-0 flex-1 truncate text-sm font-medium text-charcoal hover:text-burnt-orange dark:text-charcoal-dark dark:hover:text-burnt-orange-dark"
                >
                  {i.name}
                </Link>
                <span className="font-mono text-xs tabular-nums text-muted-foreground">
                  {i.current_quantity} / {i.low_stock_threshold ?? '–'} {i.unit}
                </span>
              </div>
              {pct !== null ? (
                <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-surface-raised dark:bg-surface-raised-dark">
                  <div
                    className="h-full bg-clay transition-all dark:bg-clay-dark"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
